import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { ChildCell, ChildRow } from './ChildData';

interface ITableProps {
  appearance?: 'default' | 'noContainer';
  cellMinWidth?: number;
  children?: ReactNode;
  className?: string;
  paddingBottom?: number;
  scrollable?: boolean;
  sticky?: boolean;
  stickyMaxWidth?: string;
}

const Layout = styled.div<ITableProps>`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;
  border-radius: ${toRem(5.5)};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  overflow-y: hidden;

  ${({ appearance }) =>
    appearance === 'noContainer' &&
    css`
      border-radius: 0;
      border: none;
    `}

  ${({ paddingBottom }) =>
    paddingBottom &&
    css`
      padding-bottom: ${toRem(paddingBottom)};
    `}
`;

const StyledTable = styled.table`
  position: relative;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  thead,
  tbody,
  tfoot {
    width: 100%;
  }
`;

const stickyCellStyles = css`
  position: sticky;
  left: 0;
  z-index: ${theme.zIndex.contentHeader - 1};
`;

const StickyTableFirstCellHoverStyles = css`
  &:first-child {
    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: ${toRem(1)};
      background-color: ${theme.color.neutral[200]};
    }
  }
`;

const ScrollableTable = styled.table<ITableProps>`
  position: relative;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  overflow-x: auto;

  thead,
  tbody,
  tfoot {
    width: 100%;
  }

  ${({ cellMinWidth }) =>
    cellMinWidth &&
    css`
      tbody {
        tr {
          td {
            min-width: ${toRem(cellMinWidth || 100)};
          }
        }
      }
    `}

  ${({ sticky }) =>
    sticky &&
    css`
      thead {
        tr {
          th {
            &:first-child {
              ${stickyCellStyles}
            }
          }
        }
      }

      tbody {
        tr {
          td {
            &:first-child {
              ${stickyCellStyles}
              transition: none;
              background-color: ${theme.color.white};
            }
          }

          &:hover {
            td {
              &:first-child {
                background-color: ${theme.color.neutral[50]};
              }
            }

            ${ChildRow} {
              ${ChildCell} {
                background-color: ${theme.color.neutral[100]};
              }
            }
          }
        }
      }

      &:hover {
        thead {
          tr {
            th {
              ${StickyTableFirstCellHoverStyles}
            }
          }
        }

        tbody {
          tr {
            td {
              ${StickyTableFirstCellHoverStyles}
            }
          }
        }
      }

      @media (hover: none) {
        thead {
          tr {
            th {
              ${StickyTableFirstCellHoverStyles}
            }
          }
        }

        tbody {
          tr {
            td {
              ${StickyTableFirstCellHoverStyles}
            }
          }
        }
      }
    `}


  ${({ stickyMaxWidth }) =>
    stickyMaxWidth &&
    css`
      thead {
        tr {
          th {
            max-width: ${stickyMaxWidth};
            width: ${stickyMaxWidth};
            word-break: break-all;
            white-space: break-spaces;
          }
        }
      }

      tbody {
        tr {
          td {
            max-width: ${stickyMaxWidth};
            width: ${stickyMaxWidth};
            word-break: break-all;
            white-space: break-spaces;
            padding: ${toRem(24)};
          }
        }
      }
    `}
`;

const Table = ({ appearance = 'default', cellMinWidth, children, className, paddingBottom, scrollable, sticky, stickyMaxWidth, ...rest }: ITableProps) => (
  <Layout appearance={appearance} className={className} paddingBottom={paddingBottom} {...rest}>
    {scrollable ? (
      <ScrollableTable sticky={sticky} stickyMaxWidth={stickyMaxWidth} cellMinWidth={cellMinWidth}>
        {children}
      </ScrollableTable>
    ) : (
      <StyledTable>{children}</StyledTable>
    )}
  </Layout>
);

export default Table;
